import React from "react";
import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { Breadcrumbs } from "@/components/layout/Breadcrumbs";
import { SEO } from "@/lib/seo";
import { Button } from "@/components/ui/button";
import {
    Video,
    Stethoscope,
    ClipboardCheck,
    CalendarClock,
    MessageSquare,
    FileText,
    ShieldCheck,
    Globe2,
    Clock,
    ArrowRight,
    Smartphone,
    Building2,
    Timer,
    Mail,
    Calendar,
    AlertCircle,
} from "lucide-react";
import { ReferralRequiredNotice } from "@/components/sections/ReferralRequiredNotice";

const STEP_ICONS = [Stethoscope, ClipboardCheck, CalendarClock, MessageSquare, FileText];

export default function VirtualConsultationsPage() {
    const { t } = useTranslation();
    const steps = [1, 2, 3, 4, 5].map((n, i) => ({
        icon: STEP_ICONS[i],
        title: t(`virtual.step${n}Title`),
        body: t(`virtual.step${n}Body`),
    }));
    const benefits = [
        { icon: Globe2, title: t("virtual.benefit1Title"), body: t("virtual.benefit1Body") },
        { icon: Clock, title: t("virtual.benefit2Title"), body: t("virtual.benefit2Body") },
        { icon: ShieldCheck, title: t("virtual.benefit3Title"), body: t("virtual.benefit3Body") },
    ];
    const idealFor = [1, 2, 3, 4, 5, 6].map((n) => t(`virtual.idealFor${n}`));
    const notIdeal = [1, 2, 3].map((n) => t(`virtual.notIdeal${n}`));

    return (
        <>
            <SEO title={t("virtual.seoTitle")} description={t("virtual.seoDescription")} />
            <div className="container-page pt-8">
                <Breadcrumbs items={[{ label: t("virtual.crumb") }]} />
            </div>

            <section className="container-page pt-6 pb-4">
                <div className="grid gap-10 lg:grid-cols-12 items-start">
                    <div className="lg:col-span-7">
                        <span className="chip-teal">
                            <Video className="h-3.5 w-3.5" /> {t("virtual.chip")}
                        </span>
                        <h1 className="mt-3 font-display text-4xl sm:text-5xl font-semibold text-[color:var(--brand-navy)] leading-tight">
                            {t("virtual.title")}
                        </h1>
                        <p className="mt-5 text-[color:var(--text-muted)] leading-relaxed text-lg">
                            {t("virtual.intro")}
                        </p>
                        <div className="mt-6 flex flex-col sm:flex-row gap-3">
                            <Button asChild size="lg" data-testid="virtual-cta-primary">
                                <Link to="/referrals" className="gap-1.5">
                                    {t("cta.howReferralsWork")}
                                    <ArrowRight className="h-4 w-4" />
                                </Link>
                            </Button>
                        </div>
                        <p className="mt-4 text-xs text-[color:var(--text-soft)]">
                            {t("virtual.selfReferralNote")}
                        </p>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="card-soft p-6 grain" style={{ background: "linear-gradient(135deg, rgba(14,124,134,0.14) 0%, rgba(255,255,255,1) 60%)" }}>
                            <div className="grid grid-cols-1 gap-3">
                                {benefits.map((b) => (
                                    <div key={b.title} className="rounded-2xl bg-white border border-[color:var(--border-default)] p-4 flex items-start gap-3">
                                        <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[color:var(--brand-teal-soft)] text-[color:var(--brand-teal-2)] shrink-0">
                                            <b.icon className="h-4 w-4" />
                                        </span>
                                        <div>
                                            <p className="text-sm font-semibold text-[color:var(--brand-navy)] leading-tight">{b.title}</p>
                                            <p className="mt-1 text-xs text-[color:var(--text-muted)] leading-relaxed">{b.body}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <div className="pb-4">
                <ReferralRequiredNotice />
            </div>

            <section className="container-page section-y">
                <div className="max-w-3xl">
                    <span className="chip-teal">{t("virtual.howItWorksChip")}</span>
                    <h2 className="mt-3 font-display text-3xl sm:text-4xl font-semibold text-[color:var(--brand-navy)]">
                        {t("virtual.howItWorksTitle")}
                    </h2>
                    <p className="mt-3 text-[color:var(--text-muted)] leading-relaxed">
                        {t("virtual.howItWorksIntro")}
                    </p>
                </div>

                <ol className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="virtual-steps">
                    {steps.map((s, i) => (
                        <li key={s.title} className="card-soft p-6">
                            <div className="flex items-center gap-3">
                                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full text-white font-semibold" style={{ background: "var(--brand-teal)", boxShadow: "0 10px 30px rgba(14,124,134,0.18)" }}>
                                    {i + 1}
                                </span>
                                <s.icon className="h-5 w-5 text-[color:var(--brand-teal-2)]" />
                            </div>
                            <h3 className="mt-4 font-display text-lg font-semibold text-[color:var(--brand-navy)]">{s.title}</h3>
                            <p className="mt-2 text-sm text-[color:var(--text-muted)] leading-relaxed">{s.body}</p>
                        </li>
                    ))}
                </ol>
            </section>

            <section className="container-page pb-4">
                <div className="max-w-3xl">
                    <span className="chip-teal">
                        <Mail className="h-3.5 w-3.5" /> {t("virtual.oceanChip")}
                    </span>
                    <h2 className="mt-3 font-display text-3xl sm:text-4xl font-semibold text-[color:var(--brand-navy)]">
                        {t("virtual.oceanTitle")}
                    </h2>
                    <p className="mt-3 text-[color:var(--text-muted)] leading-relaxed">
                        <Trans i18nKey="virtual.oceanIntro" components={{ 1: <strong className="text-[color:var(--brand-navy)]" /> }} />
                    </p>
                </div>

                <div className="mt-8 grid gap-8 lg:grid-cols-12 items-start">
                    <div className="lg:col-span-7">
                        <div className="rounded-2xl border border-[color:var(--border-default)] bg-white overflow-hidden" style={{ boxShadow: "0 20px 60px rgba(15,39,71,0.10)" }} data-testid="ocean-email-preview">
                            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[color:var(--border-default)] bg-[color:var(--bg-subtle)]">
                                <div className="flex gap-1.5">
                                    <span className="h-2.5 w-2.5 rounded-full bg-red-400" aria-hidden />
                                    <span className="h-2.5 w-2.5 rounded-full bg-yellow-400" aria-hidden />
                                    <span className="h-2.5 w-2.5 rounded-full bg-green-400" aria-hidden />
                                </div>
                                <span className="ml-auto text-[11px] font-medium text-[color:var(--text-soft)]">
                                    {t("virtual.emailSentVia")}
                                </span>
                            </div>
                            <div className="px-5 py-4 border-b border-[color:var(--border-default)] space-y-1.5 text-sm">
                                <div className="flex gap-2">
                                    <span className="text-[color:var(--text-soft)] w-16 shrink-0">{t("virtual.emailFrom")}</span>
                                    <span className="text-[color:var(--brand-navy)] font-medium">
                                        Dr. Kichian's Office &lt;noreply@oceanmd.com&gt;
                                    </span>
                                </div>
                                <div className="flex gap-2">
                                    <span className="text-[color:var(--text-soft)] w-16 shrink-0">{t("virtual.emailSubject")}</span>
                                    <span className="text-[color:var(--brand-navy)] font-semibold">
                                        {t("virtual.emailSubjectValue")}
                                    </span>
                                </div>
                            </div>
                            <div className="px-5 py-5 text-sm text-[color:var(--text-primary)] leading-relaxed space-y-3">
                                <p>{t("virtual.emailGreeting")}</p>
                                <p><Trans i18nKey="virtual.emailBody1" components={{ 1: <strong /> }} /></p>
                                <p><Trans i18nKey="virtual.emailBody2" components={{ 1: <strong /> }} /></p>
                                <div className="mt-3 rounded-xl border border-[color:var(--brand-teal-2)]/25 bg-[color:var(--brand-teal-soft)] p-4" data-testid="ocean-email-appointment-block">
                                    <p className="text-[11px] uppercase tracking-wider font-semibold text-[color:var(--brand-teal-2)] mb-2">
                                        {t("virtual.appointmentDetails")}
                                    </p>
                                    <dl className="grid grid-cols-[80px_1fr] gap-x-3 gap-y-1 text-sm">
                                        <dt className="text-[color:var(--text-muted)]">{t("virtual.date")}</dt>
                                        <dd className="font-semibold text-[color:var(--brand-navy)]">Aug 3, 2026</dd>
                                        <dt className="text-[color:var(--text-muted)]">{t("virtual.time")}</dt>
                                        <dd className="font-semibold text-[color:var(--brand-navy)]">2:00 PM</dd>
                                        <dt className="text-[color:var(--text-muted)]">{t("virtual.provider")}</dt>
                                        <dd className="font-semibold text-[color:var(--brand-navy)]">K Kichian</dd>
                                    </dl>
                                </div>
                                <p className="text-xs text-[color:var(--text-soft)] italic pt-1">
                                    {t("virtual.emailNote")}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-5 space-y-4">
                        <div className="card-soft p-5">
                            <div className="flex items-center gap-2">
                                <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[color:var(--brand-teal-soft)] text-[color:var(--brand-teal-2)]">
                                    <Calendar className="h-4 w-4" />
                                </span>
                                <h3 className="font-display text-base font-semibold text-[color:var(--brand-navy)]">
                                    {t("virtual.whatEmailMeansTitle")}
                                </h3>
                            </div>
                            <p className="mt-2 text-sm text-[color:var(--text-muted)] leading-relaxed">
                                {t("virtual.whatEmailMeansBody")}
                            </p>
                        </div>

                        <div className="card-soft p-5">
                            <div className="flex items-center gap-2">
                                <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[color:var(--brand-navy)]/8 text-[color:var(--brand-navy)]">
                                    <Building2 className="h-4 w-4" />
                                </span>
                                <h3 className="font-display text-base font-semibold text-[color:var(--brand-navy)]">
                                    {t("virtual.proceduresBookedTitle")}
                                </h3>
                            </div>
                            <p className="mt-2 text-sm text-[color:var(--text-muted)] leading-relaxed">
                                <Trans i18nKey="virtual.proceduresBookedBody" components={{ 1: <strong className="text-[color:var(--brand-navy)]" /> }} />
                            </p>
                        </div>

                        <div className="rounded-2xl border p-5 flex items-start gap-3" style={{ background: "rgba(180,83,9,0.06)", borderColor: "rgba(180,83,9,0.25)" }} data-testid="ocean-junk-mail-warning">
                            <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" style={{ color: "var(--warning)" }} />
                            <div>
                                <p className="font-semibold text-[color:var(--brand-navy)] text-sm">
                                    {t("virtual.junkTitle")}
                                </p>
                                <p className="mt-1 text-sm text-[color:var(--text-muted)] leading-relaxed">
                                    <Trans i18nKey="virtual.junkBody" components={{ 1: <strong /> }} />
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="container-page pb-4">
                <div className="card-soft p-6 sm:p-8 grain" data-testid="virtual-platform-info" style={{ background: "linear-gradient(135deg, rgba(14,124,134,0.10) 0%, rgba(255,255,255,1) 60%)" }}>
                    <div className="grid gap-8 lg:grid-cols-12 items-start">
                        <div className="lg:col-span-4">
                            <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-[color:var(--brand-teal-2)] border border-[color:var(--brand-teal-2)]/25">
                                <Smartphone className="h-5 w-5" />
                            </span>
                            <p className="mt-4 text-[11px] uppercase tracking-wider text-[color:var(--brand-teal-2)] font-semibold">
                                {t("virtual.platformChip")}
                            </p>
                            <h2 className="mt-1 font-display text-2xl sm:text-3xl font-semibold text-[color:var(--brand-navy)] leading-tight">
                                {t("virtual.platformTitle")}
                            </h2>
                        </div>
                        <div className="lg:col-span-8">
                            <p className="text-[color:var(--text-muted)] leading-relaxed">
                                <Trans i18nKey="virtual.platformIntro" components={{ 1: <strong className="text-[color:var(--brand-navy)]" /> }} />
                            </p>
                            <ul className="mt-4 space-y-2.5 text-sm text-[color:var(--text-muted)] leading-relaxed">
                                <li className="flex items-start gap-2">
                                    <Timer className="h-4 w-4 text-[color:var(--brand-teal-2)] shrink-0 mt-0.5" />
                                    <span><Trans i18nKey="virtual.platform1" components={{ 1: <strong className="text-[color:var(--brand-navy)]" /> }} /></span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <Smartphone className="h-4 w-4 text-[color:var(--brand-teal-2)] shrink-0 mt-0.5" />
                                    <span>{t("virtual.platform2")}</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <ShieldCheck className="h-4 w-4 text-[color:var(--brand-teal-2)] shrink-0 mt-0.5" />
                                    <span>{t("virtual.platform3")}</span>
                                </li>
                            </ul>
                            <p className="mt-4 text-xs text-[color:var(--text-soft)]">
                                {t("virtual.platformNote")}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="container-page pb-8">
                <div className="card-soft p-6 sm:p-8" data-testid="virtual-inperson-option" style={{ background: "linear-gradient(135deg, rgba(15,39,71,0.06) 0%, rgba(255,255,255,1) 60%)" }}>
                    <div className="grid gap-6 lg:grid-cols-12 items-start">
                        <div className="lg:col-span-4">
                            <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-[color:var(--brand-navy)] border border-[color:var(--border-default)]">
                                <Building2 className="h-5 w-5" />
                            </span>
                            <p className="mt-4 text-[11px] uppercase tracking-wider text-[color:var(--brand-navy)] font-semibold">
                                {t("virtual.inPersonChip")}
                            </p>
                            <h2 className="mt-1 font-display text-2xl sm:text-3xl font-semibold text-[color:var(--brand-navy)] leading-tight">
                                {t("virtual.inPersonTitle")}
                            </h2>
                        </div>
                        <div className="lg:col-span-8">
                            <p className="text-[color:var(--text-muted)] leading-relaxed">
                                <Trans i18nKey="virtual.inPersonBody" components={{ 1: <strong className="text-[color:var(--brand-navy)]" />, 3: <strong className="text-[color:var(--brand-navy)]" /> }} />
                            </p>
                            <p className="mt-3 text-[color:var(--text-muted)] leading-relaxed">
                                {t("virtual.inPersonNote")}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="container-page pb-8">
                <div className="grid gap-6 lg:grid-cols-2">
                    <div className="card-soft p-6">
                        <h2 className="font-display text-xl font-semibold text-[color:var(--brand-navy)]">
                            {t("virtual.greatFor")}
                        </h2>
                        <ul className="mt-3 space-y-2 text-sm text-[color:var(--text-muted)] leading-relaxed pl-5 list-disc marker:text-[color:var(--brand-teal)]">
                            {idealFor.map((x) => (<li key={x}>{x}</li>))}
                        </ul>
                    </div>
                    <div className="card-soft p-6" style={{ background: "rgba(180,83,9,0.05)" }}>
                        <h2 className="font-display text-xl font-semibold text-[color:var(--brand-navy)]">
                            {t("virtual.notIdealTitle")}
                        </h2>
                        <ul className="mt-3 space-y-2 text-sm text-[color:var(--text-muted)] leading-relaxed pl-5 list-disc marker:text-[color:var(--warning)]">
                            {notIdeal.map((x) => (<li key={x}>{x}</li>))}
                        </ul>
                        <p className="mt-4 text-xs text-[color:var(--text-soft)]">
                            {t("virtual.notIdealNote")}
                        </p>
                    </div>
                </div>
            </section>

            <section className="container-page pb-8">
                <div className="card-soft p-8 grain" style={{ background: "linear-gradient(135deg, rgba(15,39,71,0.06) 0%, rgba(255,255,255,1) 60%)" }}>
                    <div className="flex items-center gap-2">
                        <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[color:var(--brand-teal-soft)] text-[color:var(--brand-teal-2)]">
                            <ShieldCheck className="h-5 w-5" />
                        </span>
                        <h2 className="font-display text-2xl font-semibold text-[color:var(--brand-navy)]">
                            {t("virtual.privacyTitle")}
                        </h2>
                    </div>
                    <p className="mt-3 text-[color:var(--text-muted)] leading-relaxed max-w-3xl">
                        {t("virtual.privacyBody")}
                    </p>
                </div>
            </section>
        </>
    );
}
